import React, { MouseEventHandler, ReactNode, useState, useImperativeHandle, forwardRef, useEffect } from "react";
import IconButton from "./IconButton";
import SVG from "src/utils/SVG";
import Button from "./Button";
import CheckBox from "./CheckBox";
import ModalPortal from "src/modalPortal";
import styled from "styled-components";
import { CSSTransition } from "react-transition-group";

interface IBottomModalProps {
  modalTitle?: string;
  children: ReactNode;
  className?: string;
  headerBtn?: string;
  checkBox?: string;
  checked?: boolean;
  footerBtn?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>; // 헤더버튼
  onClick2?: MouseEventHandler<HTMLButtonElement>; // 푸터버튼
  onClick3?: MouseEventHandler<HTMLButtonElement>; // 닫기버튼
  onChange?: (e: boolean) => void;
  dimClickFun?: any;
  initVisible?: boolean;
}
export interface RefProps {
  modalOpen: () => void;
  modalClose: () => void;
}

const BottomModal = (props: IBottomModalProps, ref: React.Ref<RefProps>) => {
  const [visible, setVisible] = useState(false);

  const [initVisible, setInitVisible] = useState(false);

  // ====================================== hook 영역 ======================================
  useEffect(() => {
    setInitVisible(props.initVisible!);
  }, [props]);

  // ====================================== 퍼블리싱 영역 ======================================
  const onModalOpen = () => {
    setVisible(true);
    // 모달창 열 때 바디 스크롤 잠금
    document.body.style.overflow = "hidden";
  };

  const onModalClose = () => {
    // 모달창 닫을 때 바디 스크롤 풀림
    document.body.style.overflow = "unset";
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    modalOpen: () => onModalOpen(),
    modalClose: () => onModalClose(),
  }));

  const clickDimmed = () => {
    // 모달창 닫을 때 바디 스크롤 풀림
    document.body.style.overflow = "unset";
    setVisible(false);
    if (props.dimClickFun) {
      props.dimClickFun();
    }
  };

  // ====================================== hook 영역 ======================================

  return (
    <CSSTransition in={visible} classNames="bottomModal" timeout={400} unmountOnExit>
      <ModalPortal>
        <ModalOverlay className={visible ? "open-modal" : ""}>
          <ModalDim onClick={clickDimmed}></ModalDim>
          <ModalWrapper className="modal-wrap">
            <ModalHeader>
              <div>
                <h3 className="title">{props.modalTitle}</h3>
                {props.headerBtn && (
                  <Button variant="contained" color={initVisible ? "active" : "secondary"} br="round" size="small" onClick={props.onClick}>
                    {props.headerBtn}
                  </Button>
                )}
                {props.checkBox && <CheckBox id="basic-check1" label="무상" type="round" checked={props.checked} onChange={props.onChange} />}
              </div>
              <IconButton
                btnName="팝업닫기"
                onClick={(e) => {
                  onModalClose(), props.onClick3?.(e);
                }}
              >
                <SVG name="modalClose" width={24} height={24} viewBox="0 2 24 24" />
              </IconButton>
            </ModalHeader>
            <ModalBody className={props.footerBtn ? "isFooterBtn" : ""}>
              <div>{props.children}</div>
            </ModalBody>
            {props.footerBtn && (
              <ModalFooter>
                <Button variant="contained" br="round" onClick={props.onClick2}>
                  {props.footerBtn}
                </Button>
              </ModalFooter>
            )}
          </ModalWrapper>
        </ModalOverlay>
      </ModalPortal>
    </CSSTransition>
  );
};

const ModalOverlay = styled.div`
  z-index: 2000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  ${({ theme }) => theme.flexBox.flexMixin("column", "flex-end")};
`;
const ModalDim = styled.div`
  z-index: 2001;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(60, 60, 60, 0.5);
`;
const ModalWrapper = styled.div`
  z-index: 2002;
  position: relative;
  overflow: hidden;
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100% - ${({ theme }) => theme.mobile.notchHeight}px);
  background-color: #fff;
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 18px 15px 0 15px;
  height: 55px;
  > button {
    position: absolute;
    top: 18px;
    right: 15px;
  }
  > div {
    height: 37px;
    padding: 0 40px 12px 0;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    .title {
      font-size: 16px;
    }
    ${({ theme }) => theme.flexBox.flexMixin()};
  }
`;

const ModalBody = styled.div`
  overflow: hidden;
  overflow-y: auto;
  max-height: calc(100% - 55px);
  &.isFooterBtn {
    max-height: calc(100% - 135px);
  }
`;

const ModalFooter = styled.div`
  padding: 20px 15px;
`;

export default forwardRef(BottomModal);
